import 'Styles/stylesheet.less';
import React from 'react';
import { withRouter } from 'react-router';
import LocalizedStrings from 'localized-strings';
import * as utils from 'Utils';
import * as analytics from 'Utils/analytics';
import { getInterestsStatus } from 'Services/interests';
import Router from './Routes';
const { EVENTS } = analytics;
const { ROUTES, ENTRY_PAGES } = utils;
// prettier-ignore
const DEFAULT_LOGIN_PROVIDERS = process.env.REACT_APP_DEFAULT_LOGIN_PROVIDERS.split(', ');
const FORCE_LANGUAGE = process.env.REACT_APP_FORCE_LANGUAGE;
const ACTIVATION_APPLICATION_ID = process.env.REACT_APP_ACTIVATION_APPLICATION_ID.toLowerCase();
const DEEPLINK_SCHEME = process.env.REACT_APP_DEEPLINK_SCHEME;
const DEEPLINK_LOGIN = process.env.REACT_APP_DEEPLINK_LOGIN;
const ENABLE_INTERESTS = process.env.REACT_APP_ENABLE_INTERESTS === 'true';
const UIM_SDK_ENV = process.env.REACT_APP_UIM_SDK_ENV;
const IS_CLIENT = utils.isBrowser();
let loginUIM = IS_CLIENT ? require('Utils/uim').default : {};

utils.initSentry();

class App extends React.Component {
  state = {
    activationCode: null,
    application: '',
    applicationConfig: {
      exists: false,
      loginProviders: DEFAULT_LOGIN_PROVIDERS,
    },
    country: 'US',
    client_id: '',
    darkTheme: false,
    disableSuccessRedirect: false,
    email: '',
    failedLogin: false,
    interestsOnboardingComplete: false,
    isUimLoaded: false,
    language: window?.navigator?.language?.slice(0, 2) || 'en',
    launchProvider: '',
    loading: false,
    locale: 'US',
    looped: false,
    notificationIcon: 'checkbox',
    notificationMessage: '',
    notificationMessageStringKey: '',
    providers: [],
    redirectTo: '',
    registrationItems: {},
    registrationVerificationUIMToken: null,
    resetPasswordToken: '',
    server_state: '',
    strings: new LocalizedStrings({ en: {} }),
    uimRegistrationAccepted: false,
  };

  componentDidMount = async () => {
    document.body.dataset.theme = utils.getTenantConfig().theme;
    const query = utils.getQueryObject(this.props);
    try {
      // Pass the query params early on so that UIM SDK can pickup the correct region.
      const state = await this.processQueryParams(query);
      const { application, country, language } = state;
      // console.log('--- Application State ---', state);
      this.setState(state, async () => {
        if (IS_CLIENT) {
          await this.initializeClient(query, application, country, language);
          // When looped we want to start on the email validation screen
          if (query.looped) {
            this.transitionTo(ROUTES.PAGE_VALIDATE.url);
          }
        }
      });
    } catch (err) {
      utils.logException(err);
    }
  };

  processQueryParams = async (params) => {
    try {
      // If there is a reset password token in the url show the new password page
      if (params.uimResetPasswordToken) {
        this.transitionTo(ROUTES.PAGE_FORGOT_PASSWORD_CALLBACK.url, {
          resetPasswordToken: params.uimResetPasswordToken,
        });
      }

      if (params.darkTheme === 'true') {
        document.body.classList.add('darkTheme');
      }

      // Set campaign ID for SDK - application id and client_id are used in the same way
      const token = params.application || params.client_id;
      const country = (params.country || this.state.country).toUpperCase();
      // Force English when in US across the board
      let language = (country === 'US'
        ? 'EN'
        : params.language || this.state.language
      ).toLowerCase();
      // Check config if we need to force a language
      if (FORCE_LANGUAGE) {
        language = FORCE_LANGUAGE.toLowerCase();
      }
      const locale = country === 'US' ? 'US' : 'ROW';
      // Set campaign ID for SDK - application id and client_id are used in the same way
      const initToken = async (token) => {
        let config = { ...this.state.applicationConfig };
        if (token) {
          const res = await utils.postMessageHelper.initToken(token);
          if (res) {
            config = {
              ...res,
              exists: true,
            };
          }
        }
        return config;
      };
      let applicationConfig = await initToken(token);

      // No matter what if this is an OAuth workflow only allow e-mail for now
      if (params.oauth) {
        applicationConfig = {
          ...applicationConfig,
          exists: true,
          loginProviders: ['email'],
          disallowRegistration:
            params.disallowRegistration === 'true' ||
            params.disallowRegistration === '1',
        };
      }

      // Set the localized translations
      const strings = new LocalizedStrings(
        utils.getTranslation(
          utils.getTenantConfig().translations,
          language,
          country
        ),
        { logsEnabled: false }
      );
      strings.setLanguage(language);

      // Pull analytics from params and initialize
      analytics.init({ ...params, country, language });

      return {
        ...(params.darkTheme && { darkTheme: params.darkTheme === 'true' }),
        ...(params.email && { email: params.email }),
        ...(params.activationCode && { activationCode: params.activationCode }),
        // Test usage of this by clicking "cancel your registration", UIM's SDK does something special with it
        ...(params.registrationVerificationUIMToken && {
          registrationVerificationUIMToken:
            params.registrationVerificationUIMToken,
        }),
        ...(params.uimRegistrationAccepted && {
          uimRegistrationAccepted: params.uimRegistrationAccepted !== 'false',
        }),
        ...(params.looped && {
          looped: params.looped === 'true' || params.looped === '1',
        }),
        ...(params.redirectTo && { redirectTo: params.redirectTo }),
        ...(params.launchProvider && { launchProvider: params.launchProvider }),
        ...(params.server_state && { server_state: params.server_state }),
        ...(params.client_id && { client_id: params.client_id }),
        country,
        language,
        locale,
        strings,
        application: token,
        applicationConfig,
      };
    } catch (err) {
      utils.logException(err);
    }
  };

  async initializeClient(params, application, country, language) {
    try {
      const autoLoginTokens = this.getAutoLoginTokens(params);
      const autoRefreshTimeout = UIM_SDK_ENV === 'design' ? 0.08 : 1;
      // Fix this for social login redirect
      // Awaiting this promise to ensure we have user data
      await loginUIM.init(
        this.uimCallback,
        this.onNewPassword,
        this.onLoggedIn,
        this.onSessionInvalid,
        this.onWorkflowError,
        this.onRegistrationRejected,
        this.onAccountVerified,
        {
          application,
          country,
          language,
        },
        autoLoginTokens,
        autoRefreshTimeout
      );
      this.setState(() => ({ isUimLoaded: true }));

      // Logged in? Set user
      if (loginUIM.isLoggedIn())
        analytics.trackUser(loginUIM.getAuthenticatedUser());

      // First time, send page load event
      analytics.pageView(this.props.location.pathname);

      // Subscribe to route changes to manage analytics
      this.props.history.listen(({ pathname }) => analytics.pageView(pathname));
    } catch (err) {
      utils.logException(err);
    }
  }

  /**
   * If the valid tokens are passed in return
   */
  getAutoLoginTokens = ({ access_token, refresh_token, id_token }) => {
    if (utils.hasValidProps(access_token, refresh_token, id_token)) {
      return {
        access_token,
        refresh_token,
        id_token,
      };
    }
    return {};
  };

  uimCallback = async (isAuthenticated) => {
    if (isAuthenticated) {
      const { email } = await loginUIM.getProfileValues();
      this.setState({ email });
    } else {
      utils.postMessageHelper.send({
        type: utils.postMessageHelper.types.NO_SESSION,
        payload: 'User has no session, user must login',
      });
    }

    // If launchProvider is set simulate the button click for the provider specified
    if (
      this.state.launchProvider &&
      this.props.location.pathname === ROUTES.PAGE_HOME.url
    ) {
      this.routerCallback(this.state.launchProvider);
    }
  };

  getUserInterests = async () => {
    try {
      const { userId, userToken } = await utils.getUserInfo(loginUIM);
      // show interests to users who have not set their interests yet
      const interestsStatus = await getInterestsStatus(userId, userToken);
      if (interestsStatus) {
        this.setState({ interestsOnboardingComplete: true });
        this.redirect();
      } else {
        this.transitionToReplace(ROUTES.PAGE_SET_INTERESTS.url);
      }
    } catch (err) {
      utils.logException(err);
      this.setState({ interestsOnboardingComplete: true });
      this.redirect();
    }
  };

  onNewPassword = (token) => {
    this.transitionTo(ROUTES.PAGE_FORGOT_PASSWORD_CALLBACK.url, {
      resetPasswordToken: token,
    });
  };

  // UIM will call this event after a successful login
  onLoggedIn = async ({
    availableNewsletters,
    authenticatedUser,
    missingFields,
    missingAcceptedPolicies,
    policiesRendering,
  }) => {
    const {
      disableSuccessRedirect,
      registrationVerificationUIMToken,
    } = this.state;
    this.setState({ loading: true });
    // Capture UserID send to analytics
    if (authenticatedUser) analytics.trackUser(authenticatedUser);

    // Verification flow (TEMP: limited to Xivado integration)
    if (authenticatedUser && utils.isXivado(this.state.application)) {
      const verificationStatus = await authenticatedUser.getVerificationStatus();
      if (verificationStatus !== 'verified') {
        await loginUIM.logout();
        if (authenticatedUser.loginMethod !== 'OTHER') {
          await loginUIM.resendRegistrationVerificationEmail(this.state.email);
        }
        return this.transitionTo(ROUTES.PAGE_VERIFICATION_CODE.url);
      }
    }

    if (missingFields?.length) {
      // If there are any missing fields go to updates required page
      this.transitionToReplace(ROUTES.PAGE_UPDATES_REQUIRED.url);
    } else if (policiesRendering?.length) {
      // Convert the missing payload to what the registration items payload looks like
      // Example missing payload: { availableNewsletters: [], missingAcceptedPolicies: [], missingFields: [], policiesRendering: [] }
      const missingTransformed = {
        newsletters: availableNewsletters,
        policies: missingAcceptedPolicies,
        fields: missingFields,
        policiesRendering: policiesRendering,
      };
      const registrationItems = loginUIM.transformRegistrationItems(
        missingTransformed,
        this.state.country
      );

      // If there are any policies we need to render redirect to the terms page again
      this.transitionToReplace(ROUTES.PAGE_TERMS.url, {
        registrationItems,
      });
    } else if (disableSuccessRedirect || registrationVerificationUIMToken) {
      // Don't do anything here but reset the value
      this.setState({
        disableSuccessRedirect: false,
      });
    } else {
      // Nothing to do so redirect to login success or profile
      this.redirect();
    }
  };

  onSessionInvalid = (uimApi) => {
    this.failureMessageStringKey();
    this.setState({ disableSuccessRedirect: true });
    uimApi.logout();
  };

  // TODO: we need a better way of displaying error messages.
  onWorkflowError = (e) => {
    utils.logException(e);
    if (e.code.includes('user.denied')) this.transitionTo();
    // Social login user denial
    else this.failureMessageStringKey(); // Common error
  };

  // Triggered when user clicks on cancel your registration link in email
  onRegistrationRejected = (uimApi) => {
    this.failureMessageStringKey('accountDeleted');
    this.setState({ disableSuccessRedirect: true });
    uimApi.logout();
  };

  // Triggered when user clicks on account verification link in email
  onAccountVerified = () => {
    this.transitionTo(ROUTES.PAGE_NOTIFICATION.url, {
      notificationMessageStringKey: 'accountCreated',
      notificationIcon: 'success',
      disableSuccessRedirect: true,
    });
  };

  /**
   *
   * @param loginType - facebook, google, apple-id or email
   * @param useNativeLogin - set to true if we want to trigger the native social login in a mobile app
   */
  routerCallback = async (loginType, useNativeLogin = true) => {
    analytics.event({ action: EVENTS.continue, label: loginType });
    switch (loginType) {
      case 'facebook':
      case 'google':
      case 'apple-id':
        if (useNativeLogin && utils.isMobileApp(this.state.applicationConfig)) {
          // Redirect to app to trigger the native mobile social login (special logic is built into the app to handle this)
          window.location.href = `${DEEPLINK_SCHEME}${DEEPLINK_LOGIN}?provider=${loginType}`;
        } else {
          // Continue with UIM SDK on web
          const redirectUrl = utils.appendQueryString(
            ROUTES.PAGE_LOGGING_IN.url
          );
          loginUIM.uimApi.socialLoginWithRedirect(loginType, redirectUrl);
        }
        break;
      case 'email':
      default:
        this.transitionTo(ROUTES.PAGE_VALIDATE.url);
        break;
    }
  };

  handleUserRegistration = async (user) => {
    try {
      // If, and only if, the RBAccount UIM application is configured to support "login-after-registration" ("auto-login")
      // the promise returns the authenticated user, otherwise void is returned.
      const registration = await loginUIM.register(user);

      // if the application does not support "login-after-registration"
      // redirect users to the verification code page.
      if (!registration) {
        this.transitionTo(ROUTES.PAGE_VERIFICATION_CODE.url);
      }
    } catch (e) {
      utils.logException(e);
      this.failureMessageStringKey();
    }
  };

  handleUserLogin = async (email, pw) => {
    try {
      const authenticatedUser = await loginUIM.login(email, pw);
      if (!authenticatedUser) {
        this.transitionTo(ROUTES.PAGE_LOGIN.url, {
          failedLogin: true,
        });
      }
    } catch (e) {
      utils.logException(e);
      this.setState({
        notificationMessage: e.message,
        failedLogin: true,
      });
    }
  };

  redirect = async () => {
    const {
      application,
      activationCode,
      applicationConfig,
      interestsOnboardingComplete,
      language,
      looped,
      redirectTo,
      server_state,
    } = this.state;
    const activationLogin = application === ACTIVATION_APPLICATION_ID;

    if (activationLogin) {
      // UIM SDK triggers onLoggedIn event after submitting terms & conditions - suppress subsequent redirects
      this.setState({ disableSuccessRedirect: true }, async () => {
        // Handle activation
        this.transitionToReplace(ROUTES.ACTIVATION_CODE.url);
      });

      // Don't also redirect to login success
      return;
    } else if (looped) {
      // Handle looped experience
      this.transitionToReplace(ROUTES.WELCOME_BACK.url);
      return;
    }

    const interestsOnboarding =
      ENABLE_INTERESTS &&
      applicationConfig.interestsOnboarding &&
      language === 'en' &&
      !interestsOnboardingComplete;
    if (interestsOnboarding) {
      this.getUserInterests();
      return;
    }

    if (!ENTRY_PAGES.includes(this.props.location.pathname)) {
      const popup = utils.postMessageHelper.checkForSpawner() && !redirectTo;
      if (
        redirectTo ||
        popup ||
        server_state || // indicates oauth flow
        utils.isMobileApp(applicationConfig) ||
        utils.isBevy(applicationConfig)
      ) {
        // Redirect to login success page if users need to be redirected further
        this.transitionToReplace(ROUTES.PAGE_LOGIN_SUCCESS.url);
      } else {
        // Redirect to profile page
        this.transitionToReplace(ROUTES.PAGE_PROFILE.url);
      }
    }
  };

  transitionTo = (url = ROUTES.PAGE_HOME.url, state, replace = false) => {
    const queryString = utils.getQueryString();
    const redirectUrl = queryString
      ? utils.appendQueryString(url, queryString)
      : url;

    // Don't bother going to the same url
    if (utils.appendQueryString(this.props.location.pathname) === redirectUrl)
      return;

    if (state) this.setState(state);

    if (replace) {
      this.props.history.replace(redirectUrl);
    } else {
      this.props.history.push(redirectUrl);
    }
  };

  transitionToReplace = (url = ROUTES.PAGE_HOME.url, state) => {
    this.transitionTo(url, state, true);
  };

  failureMessage = (msg = '', msgKey = 'errorMessage') => {
    this.transitionTo(ROUTES.PAGE_NOTIFICATION.url, {
      notificationMessage: msg,
      notificationMessageStringKey: msgKey,
      notificationIcon: 'error',
    });
  };

  failureMessageStringKey = (msgKey = 'errorMessage') => {
    this.failureMessage('', msgKey);
  };

  render() {
    const {
      applicationConfig,
      country,
      darkTheme,
      language,
      email,
      failedLogin,
      isUimLoaded,
      loading,
      locale,
      looped,
      notificationIcon,
      notificationMessage,
      notificationMessageStringKey,
      providers,
      registrationItems,
      resetPasswordToken,
      server_state,
      strings,
    } = this.state;

    return (
      <Router
        {...this.props}
        applicationConfig={applicationConfig}
        country={country}
        darkTheme={darkTheme}
        email={email}
        failureMessage={this.failureMessage}
        icon={notificationIcon}
        isUimLoaded={isUimLoaded}
        language={language}
        loading={loading}
        locale={locale}
        loginFailed={failedLogin}
        looped={looped}
        message={notificationMessage}
        messageStringKey={notificationMessageStringKey}
        providers={providers}
        redirect={this.redirect}
        registerUser={this.handleUserRegistration}
        registrationItems={registrationItems}
        routerCallback={this.routerCallback}
        server_state={server_state}
        strings={strings}
        token={resetPasswordToken}
        transitionTo={this.transitionTo}
        uim={loginUIM}
        userLogin={this.handleUserLogin}
      />
    );
  }
}

export default withRouter(App);
